import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Breadcrumb,
    Button,
    Dropdown,
    DropdownMenuItem,
    Input,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
    useNotifications,
} from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AddPartnersToListDocument,
    PartnerListDocument,
    PartnerListsDocument,
    RemovePartnerFromListDocument,
    SetPartnerListAnonymityDocument,
} from '../../graphql/__generated__/types';
import AddPartnersDialog from './AddPartnersDialog';
import ArchivePartnerListDialog from './ArchivePartnerListDialog';
import { partnerListRefusalOf } from './refusals';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The sheet of a partner list: PCG-276, US-06 to US-13.
 *
 * **THE BACK-OFFICE ALWAYS SEES THE COMPANY NAME, anonymous list included**, and this
 * screen is where that matters most. The anonymity of RG-PTN-05 is a client-side rule: an
 * operator curating a list of `PTN-XXXX` identifiers could not work. The tag says which
 * mode is in force; it never hides anything here.
 *
 * **Two counters, two facts, and the search only touches one.** The chip counts the whole
 * list; the rows are what the search retains, server-side. Reading the chip off
 * `partners.length` would have made the screen contradict itself on the first keystroke.
 *
 * **A partner that stops being eligible disappears on its own** (RG-PTN-03) and **nothing
 * says so**: no badge, no highlight, and no trace in the MVP. That is a decision of
 * 24/08/2026, assumed rather than forgotten, and it means a list can lose partners with
 * nothing on this screen explaining it. Do not add a « radié » badge back: the partner is
 * no longer in the list, so there is nothing to flag.
 *
 * **The feed tag is not a control.** How a list is filled is fixed at declaration
 * (RG-PTN-06), so the Actions menu holds exactly two entries: the display toggle and the
 * archiving. Every MVP list is « Manuelle », and the tag is rendered anyway because the
 * mockup does and because it is what tells an operator that the button below is not
 * missing on some other list.
 */
const PartnerListDetailPage = () => {
    const { partnerListId = '' } = useParams();
    const { createNotification } = useNotifications();

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [adding, setAdding] = useState(false);
    const [archiving, setArchiving] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, loading, error } = useQuery(PartnerListDocument, {
        variables: { partnerListId, search: debouncedSearch || null },
        fetchPolicy: 'cache-and-network',
    });

    const [setAnonymity, { loading: togglingDisplay, error: displayError }] = useMutation(
        SetPartnerListAnonymityDocument,
        { refetchQueries: [PartnerListsDocument] }
    );
    const [removePartner, { error: removeError }] = useMutation(RemovePartnerFromListDocument, {
        refetchQueries: [PartnerListDocument, PartnerListsDocument],
    });
    const [addPartners] = useMutation(AddPartnersToListDocument, {
        refetchQueries: [PartnerListDocument, PartnerListsDocument],
    });

    const list = data?.partnerList ?? null;

    const toggleDisplay = async () => {
        if (!list) {
            return;
        }
        const next = !list.isAnonymous;
        try {
            await setAnonymity({ variables: { partnerListId, isAnonymous: next } });
            createNotification({
                type: 'success',
                text: next
                    ? c('Partner lists').t`List switched to anonymous`
                    : c('Partner lists').t`List switched to nominative`,
            });
        } catch {
            // Rendered under the header from `displayError`.
        }
    };

    /**
     * The removal, with its undo. The toast carries the button because that is the only
     * place the gesture is offered: there is no confirmation before, on purpose
     * (RG-PTN-13).
     *
     * **The undo is an ADDITION, not a restoration**, and it is exact because the
     * `PTN-XXXX` lives on the partner and never on the attachment: re-adding gives the
     * list back identically. Nothing needs to be kept aside for it.
     */
    const remove = async (sourcePartnerId: string, name: string) => {
        try {
            await removePartner({ variables: { partnerListId, sourcePartnerId } });
        } catch {
            return;
        }
        createNotification({
            type: 'success',
            expiration: 8000,
            text: (
                <span className="flex flex-wrap items-center gap-2">
                    {c('Partner lists').t`Partner removed from the list`}
                    <Button
                        category="link"
                        size="small"
                        onClick={() => {
                            void addPartners({ variables: { partnerListId, sourcePartnerIds: [sourcePartnerId] } })
                                .then(() =>
                                    createNotification({
                                        type: 'success',
                                        text: c('Partner lists').t`${name} is back in the list`,
                                    })
                                )
                                .catch(() =>
                                    createNotification({
                                        type: 'danger',
                                        // The re-addition can genuinely fail: the partner may
                                        // have left the eligible referential in between.
                                        text: c('Partner lists')
                                            .t`${name} could not be put back: it is no longer eligible.`,
                                    })
                                );
                        }}
                    >
                        {c('Partner lists').t`Undo`}
                    </Button>
                </span>
            ),
        });
    };

    const partnerColumns: TableHeaderCellProps[] = [
        { id: 'name', label: c('Partner lists').t`Partner name`, minWidth: 220 },
        { id: 'reference', label: c('Partner lists').t`Partner ID`, minWidth: 130 },
        { id: 'external', label: c('Partner lists').t`Salesforce external ID`, minWidth: 190 },
        { id: 'action', label: c('Partner lists').t`Action`, minWidth: 140, textAlign: 'end' },
    ];

    const holderColumns: TableHeaderCellProps[] = [
        { id: 'user', label: c('Partner lists').t`User`, minWidth: 240 },
        { id: 'account', label: c('Partner lists').t`Account`, minWidth: 260 },
        { id: 'detail', label: c('Partner lists').t`Detail`, minWidth: 140, textAlign: 'end' },
    ];

    if (!loading && !error && !list) {
        return (
            <RegionState
                tone="error"
                title={c('Partner lists').t`This list no longer exists`}
                description={c('Partner lists')
                    .t`Somebody has probably archived it. An archived list keeps its name reserved and no screen lists them.`}
            >
                <Link to="/listes-de-partenaires">
                    <Button category="weak" size="small">{c('Partner lists').t`Back to the lists`}</Button>
                </Link>
            </RegionState>
        );
    }

    if (error && !list) {
        return <RefusalNotice refusal={partnerListRefusalOf(error)} />;
    }

    const partners = list?.partners ?? [];
    const holders = list?.holders ?? [];
    const filtered = Boolean(debouncedSearch);
    const showSkeleton = loading && !data;

    return (
        <div className="flex flex-col gap-5">
            <Breadcrumb
                items={[
                    { name: c('Partner lists').t`Partner lists`, to: '/listes-de-partenaires' },
                    { name: list?.name ?? '' },
                ]}
            />

            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-semibold tracking-tight">{list?.name}</h1>
                    {list && (
                        <>
                            <Badge size="small" category={list.isAnonymous ? 'info' : 'default'}>
                                {list.isAnonymous ? c('Partner lists').t`Anonymous` : c('Partner lists').t`Nominative`}
                            </Badge>
                            {/* Fixed at declaration, so a statement and not a control. */}
                            <Badge size="small">{c('Partner lists').t`Manual`}</Badge>
                            {list.archivedAt && (
                                <Badge size="small" category="warning">
                                    {c('Partner lists').t`Archived`}
                                </Badge>
                            )}
                        </>
                    )}
                </div>
                {list && !list.archivedAt && (
                    <div className="flex flex-wrap items-center gap-2">
                        <Dropdown content={c('Partner lists').t`Actions`} buttonCategory="weak">
                            <DropdownMenuItem onClick={toggleDisplay} disabled={togglingDisplay}>
                                {list.isAnonymous
                                    ? c('Partner lists').t`Switch to nominative`
                                    : c('Partner lists').t`Switch to anonymous`}
                            </DropdownMenuItem>
                            <DropdownMenuItem tone="destructive" onClick={() => setArchiving(true)}>
                                {c('Partner lists').t`Archive the list`}
                            </DropdownMenuItem>
                        </Dropdown>
                        <Button onClick={() => setAdding(true)}>{c('Partner lists').t`Add partners`}</Button>
                    </div>
                )}
            </header>

            {/* Under the header, where the gesture was: switching to nominative uncovers
                company names to every holder, so its refusal must not be a silent no-op. */}
            {displayError && <RefusalNotice refusal={partnerListRefusalOf(displayError)} />}
            {removeError && <RefusalNotice refusal={partnerListRefusalOf(removeError)} />}

            <section className="overflow-hidden rounded-lg border border-border-norm bg-background-norm">
                <div className="flex flex-wrap items-center gap-3 border-b border-border-norm px-4 py-3">
                    <h2 className="text-sm font-semibold">{c('Partner lists').t`Partners`}</h2>
                    {list && (
                        <Badge size="small">
                            {c('Partner lists').ngettext(
                                msgid`${list.partnerCount} partner in the list`,
                                `${list.partnerCount} partners in the list`,
                                list.partnerCount
                            )}
                        </Badge>
                    )}
                    <Input
                        value={search}
                        onValue={setSearch}
                        clearable
                        prefix={<MagnifyingGlass size={16} />}
                        placeholder={c('Partner lists').t`Search a partner`}
                        aria-label={c('Partner lists').t`Search a partner in this list`}
                        className="ml-auto max-w-sm"
                    />
                </div>

                <Table>
                    <TableHeader cells={partnerColumns} />
                    <TableBody loading={showSkeleton} loadingRows={4} loadingColumns={partnerColumns.length}>
                        {partners.map((partner) => (
                            <TableRow key={partner.sourcePartnerId}>
                                <TableCell className="font-medium">{partner.name}</TableCell>
                                <TableCell className="font-mono text-xs">{partner.publicReference}</TableCell>
                                <TableCell className="font-mono text-xs">{partner.externalReference}</TableCell>
                                <TableCell className="text-end">
                                    {list?.archivedAt ? (
                                        <span className="text-text-600">—</span>
                                    ) : (
                                        <Button
                                            category="link"
                                            size="small"
                                            onClick={() => remove(partner.sourcePartnerId, partner.name)}
                                            aria-label={c('Partner lists').t`Remove ${partner.name} from this list`}
                                        >
                                            {c('Partner lists').t`Remove`}
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {!showSkeleton && partners.length === 0 && filtered && (
                    <RegionState
                        title={c('Partner lists').t`No partner in this list matches`}
                        description={c('Partner lists')
                            .t`The search runs on the company name, the PTN identifier and the Salesforce external identifier.`}
                    >
                        <Button category="weak" size="small" onClick={() => setSearch('')}>
                            {c('Partner lists').t`Clear the search`}
                        </Button>
                    </RegionState>
                )}
                {!showSkeleton && partners.length === 0 && !filtered && (
                    <RegionState
                        title={c('Partner lists').t`No partner in this list`}
                        description={c('Partner lists')
                            .t`A list is declared with at least one partner, so this means every one of them was removed, or that they left the eligible referential: a partner struck off the network leaves every list on its own, and nothing here records it.`}
                    >
                        {!list?.archivedAt && (
                            <Button size="small" onClick={() => setAdding(true)}>
                                {c('Partner lists').t`Add partners`}
                            </Button>
                        )}
                    </RegionState>
                )}
            </section>

            <section className="overflow-hidden rounded-lg border border-border-norm bg-background-norm">
                <div className="flex flex-wrap items-center gap-3 border-b border-border-norm px-4 py-3">
                    <h2 className="text-sm font-semibold">{c('Partner lists').t`Attached users`}</h2>
                    {list && (
                        <Badge size="small">
                            {c('Partner lists').ngettext(
                                msgid`${list.holderCount} user`,
                                `${list.holderCount} users`,
                                list.holderCount
                            )}
                        </Badge>
                    )}
                </div>

                <Table>
                    <TableHeader cells={holderColumns} />
                    <TableBody loading={showSkeleton} loadingRows={2} loadingColumns={holderColumns.length}>
                        {holders.map((holder) => {
                            const name = [holder.firstName, holder.lastName].filter(Boolean).join(' ');
                            return (
                                <TableRow key={holder.userId}>
                                    <TableCell>
                                        <span className="flex flex-wrap items-center gap-2">
                                            {/* The address when the name is missing: the cell
                                                always identifies somebody. */}
                                            <span className="font-medium">{name || holder.email}</span>
                                            {holder.hasLeftCompany === true && (
                                                <Badge size="small" category="danger">
                                                    {c('Partner lists').t`Has left the company`}
                                                </Badge>
                                            )}
                                            {/* Null is « the source says nothing », never « still
                                                in post »: no contact is linked to this person. */}
                                            {holder.hasLeftCompany === null && (
                                                <Badge size="small">{c('Partner lists').t`Departure unknown`}</Badge>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {holder.accountNames.length > 0 ? (
                                            holder.accountNames.join(', ')
                                        ) : (
                                            <span className="text-text-600">{c('Partner lists').t`No account`}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        <Link to={`/utilisateurs/${holder.userId}`}>
                                            <Button category="weak" size="small">
                                                {c('Partner lists').t`See the sheet`}
                                            </Button>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {!showSkeleton && holders.length === 0 && (
                    <RegionState
                        title={c('Partner lists').t`No user`}
                        description={c('Partner lists')
                            .t`A list is given to somebody from their own user sheet, at onboarding, never from here. Nobody reads this list yet, which is also why it can still be archived.`}
                    />
                )}
            </section>

            {adding && <AddPartnersDialog partnerListId={partnerListId} onClose={() => setAdding(false)} />}
            {archiving && list && (
                <ArchivePartnerListDialog
                    partnerListId={partnerListId}
                    name={list.name}
                    holderCount={list.holderCount}
                    onClose={() => setArchiving(false)}
                />
            )}
        </div>
    );
};

export default PartnerListDetailPage;
